import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

import type { AiIssueCreationEnablementType } from '../providers/IssueCreateSidePanelContextProvider.tsx';
import {
	useAiIssueCreationEnablementState,
	useIssueCreateSidePanelContext,
} from '../providers/IssueCreateSidePanelContextProvider.tsx';
import { useBulkCreateContext } from '../providers/BulkCreateContextProvider.tsx';
import { MESSAGE_PROJECT_TYPE_TO_API_PROJECT_TYPE } from '../__types__/apiUtils.ts';
import { getJiraAiEnablement } from '../services/apiUtils.ts';

type UseIsAIIssueCreationAllowedProps = {
	contentId: string;
	isBulkCreate?: boolean;
};

export const useIsAIIssueCreationAllowed = ({
	contentId,
	isBulkCreate = false,
}: UseIsAIIssueCreationAllowedProps) => {
	const [
		{
			currentSiteSelection,
			currentProjectSelection,
			isCreateManuallyPressed,
			pageMode,
			hasEditPermission,
		},
		{ setAiIssueCreationEnablement },
	] = useIssueCreateSidePanelContext();
	const [{ currentJiraSite, currentJiraProject, isCreatedFromActionItems, isCreatedFromTable }] =
		useBulkCreateContext();
	const { accessStatus, cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });
	const aiIssueCreationEnablement = useAiIssueCreationEnablementState();

	const hasLicensedConfluenceAccess =
		accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;

	// environment-aware enablement via feature encoding
	// Encoded environments example: commercial-cloud: true, fedramp-moderate: false, isolated-commercial-cloud-ga: true
	const isAIEnvEnabled = isFeatureEnabled('confluence', 'confluence-ai');

	const isConfluenceAiEnabled =
		hasLicensedConfluenceAccess && isAIEnabledForContent && isAIEnvEnabled;

	useEffect(() => {
		// isCreateManuallyPressed - When there is AI error "Create Manually" button can be pressed by user.
		// We should honour this and create manually.  This will be overriden on refresh.
		if (isCreateManuallyPressed) {
			void setAiIssueCreationEnablement({
				isEnabled: false,
			} as AiIssueCreationEnablementType);
			return;
		}

		// Short-circuit if Confluence Ai is enabled
		if (isConfluenceAiEnabled) {
			void setAiIssueCreationEnablement({
				isEnabled: true,
				enablement: 'confluence',
			} as AiIssueCreationEnablementType);
			return;
		}

		const isJiraAiEnabled = async () => {
			/**
			 * 1. Cannot call Jira enablement API if site & project are not selected
			 * 2. Remove (cloudId !== currentSiteSelection) after Jira AI API supports
			 * 	  cross-site scenarios - https://hello.jira.atlassian.cloud/browse/CBT-4995
			 */
			const bulkJiraNotSelected =
				!currentJiraSite ||
				!currentJiraProject?.projectTypeKey ||
				cloudId !== currentJiraSite.cloudId;
			const singleJiraNotSelected =
				!currentSiteSelection ||
				!currentProjectSelection?.selectedProjectType ||
				cloudId !== currentSiteSelection;
			const jiraNotSelected =
				isBulkCreate && fg('confluence-create-work-items-from-action-items')
					? bulkJiraNotSelected
					: singleJiraNotSelected;
			if (jiraNotSelected) {
				void setAiIssueCreationEnablement({
					isEnabled: false,
				} as AiIssueCreationEnablementType);
				return;
			}
			try {
				const projectType: string =
					isBulkCreate && fg('confluence-create-work-items-from-action-items')
						? (currentJiraProject?.projectTypeKey ?? '')
						: (currentProjectSelection?.selectedProjectType ?? '');
				const jiraEnablement = await getJiraAiEnablement(
					isBulkCreate && fg('confluence-create-work-items-from-action-items')
						? (currentJiraSite?.cloudId ?? '')
						: currentSiteSelection,
					MESSAGE_PROJECT_TYPE_TO_API_PROJECT_TYPE[projectType],
				);

				// 	User switches from a Jira AI enabled site to a no AI enabled site
				if (aiIssueCreationEnablement?.enablement == 'jira' && !jiraEnablement?.isEnabled) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'confluenceHighlightIssueCreate',
							action: 'disabled',
							actionSubject: 'issueCreationAi',
							attributes: {
								pageMode,
								hasEditPermission,
								isCreatedFromActionItems,
								isCreatedFromTable,
							},
						},
					}).fire();
				}

				void setAiIssueCreationEnablement({
					isEnabled: jiraEnablement?.isEnabled ?? false,
					enablement: jiraEnablement?.isEnabled ? 'jira' : undefined,
				} as AiIssueCreationEnablementType);
			} catch {
				void setAiIssueCreationEnablement({
					isEnabled: false,
				} as AiIssueCreationEnablementType);
			}
		};
		void isJiraAiEnabled();
	}, [
		currentSiteSelection,
		currentProjectSelection?.selectedProjectType,
		setAiIssueCreationEnablement,
		aiIssueCreationEnablement,
		isConfluenceAiEnabled,
		createAnalyticsEvent,
		isCreateManuallyPressed,
		cloudId,
		pageMode,
		hasEditPermission,
		currentJiraSite,
		currentJiraProject,
		isBulkCreate,
		isCreatedFromActionItems,
		isCreatedFromTable,
	]);

	return aiIssueCreationEnablement;
};
